/**
 * 文字提示组件 — 鼠标悬停时显示浮层提示信息。
 *
 * 主要导出：`CrmTooltip`（自定义元素 `<crm-tooltip>`）
 *
 * 功能特性：
 * - 鼠标移入后延迟显示提示浮层（默认 300ms）
 * - 浮层固定在宿主元素上方，带向下三角箭头
 * - 浮层使用 `position: fixed`，不受父级 overflow 裁剪影响
 * - 鼠标移出后立即隐藏
 * - 宿主元素本身支持文本省略（text-overflow: ellipsis）
 *
 * 用法：
 *   <crm-tooltip text="这是一段详细说明"><span>鼠标移上来</span></crm-tooltip>
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';

@customElement('crm-tooltip')
export class CrmTooltip extends BaseElement {
  /** 提示文本内容 */
  @property({ type: String }) text = '';
  /** 显示延迟（毫秒），默认 300ms */
  @property({ type: Number }) delay = 300;

  /** 提示浮层是否可见 */
  @state() private visible = false;
  /** 浮层水平位置（宿主元素中心 X 坐标） */
  @state() private left = 0;
  /** 浮层垂直位置（宿主元素顶部 Y 坐标） */
  @state() private top = 0;

  /** 显示延迟计时器句柄 */
  private timer: number | null = null;

  static styles = css`
    /* 宿主元素：内联块，文本溢出省略 */
    :host {
      display: inline-block;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: bottom;
      cursor: default;
    }
    /* 提示浮层：固定定位，深色背景 */
    .tooltip {
      position: fixed;
      background: #1c2024;
      color: #ffffff;
      padding: 6px 12px;
      border-radius: 6px;
      font-size: 13px;
      line-height: 1.5;
      white-space: normal;
      word-break: break-word;
      max-width: 360px;
      z-index: 99999;
      pointer-events: none;
      box-shadow: 0 4px 16px rgba(0,0,0,0.18);
    }
    /* 提示箭头：向下三角 */
    .tooltip-arrow {
      position: fixed;
      width: 0; height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid #1c2024;
      z-index: 99999;
      pointer-events: none;
    }
  `;

  /** 生命周期：连接 DOM 时注册 mouseenter / mouseleave 事件监听 */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mouseenter', this.onEnter);
    this.addEventListener('mouseleave', this.onLeave);
  }

  /** 生命周期：从 DOM 移除时移除事件监听并清除计时器 */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mouseenter', this.onEnter);
    this.removeEventListener('mouseleave', this.onLeave);
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }

  /**
   * 鼠标移入处理 — 延迟 delay 毫秒后显示提示。
   * 计算宿主元素位置，将浮层定位在元素正上方居中。
   */
  private onEnter = () => {
    if (this.timer) clearTimeout(this.timer);
    this.timer = window.setTimeout(() => {
      this.timer = null;
      const rect = this.getBoundingClientRect();
      // 浮层水平居中于宿主元素
      this.left = rect.left + rect.width / 2;
      // 浮层顶部紧贴宿主元素顶部
      this.top = rect.top;
      this.visible = true;
    }, this.delay);
  };

  /**
   * 鼠标移出处理 — 清除计时器并立即隐藏提示。
   */
  private onLeave = () => {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    this.visible = false;
  };

  /**
   * 渲染提示组件。
   * 默认渲染 slot 内容（宿主文本），visible 为 true 时额外渲染浮层和箭头。
   * 浮层通过 transform: translate(-50%, -100%) 实现居中向上偏移。
   * @returns Lit 模板
   */
  render() {
    const tooltipTop = this.top - 8;
    const arrowTop = this.top - 4;
    return html`
      <slot></slot>
      ${this.visible ? html`
        <div class="tooltip" style="left:${this.left}px; top:${tooltipTop}px; transform:translate(-50%,-100%)">
          ${this.text}
        </div>
        <div class="tooltip-arrow" style="left:${this.left}px; top:${arrowTop}px; transform:translate(-50%,-100%)"></div>
      ` : ''}
    `;
  }
}
